import { AxiosError } from 'axios';
import { readRefreshGeneration, refreshSession } from '@/services/api/refreshSession';
import { getAccountStateReason, isTerminalAccountReason } from '@/services/api/accountState';
import type { TSessionEndReason } from '@/services/endSession';
import { TUser } from '@/types/User.types';
import { fetchBootUser, fetchSessionBundle } from './sessionBundle';
import { isUserAllowedOnThisPlatform } from './platformAccess';
import { useAppStore, TSessionBundle } from '@/store/useAppStore';
import roles from '@/constants/roles';

/**
 * What the boot-time session check concluded.
 *
 * "Boot check failed" is deliberately not a single outcome. An expired-but-
 * refreshable access token is the normal state on returning to a tab, and a
 * flaky network is not a logout — collapsing these into one would turn every
 * transient API blip into a forced sign-out for every user at once.
 */
export type TBootResult =
  /** Session verified. Apply the bundle and render. */
  | { status: 'authenticated'; bundle: TSessionBundle }
  /** No usable session. The caller ends it. */
  | { status: 'signed-out'; reason?: TSessionEndReason }
  /** Signed in, but not permitted on this app. The caller ends it with a message. */
  | { status: 'wrong-platform' }
  /** The API could not answer. Retryable — never destructive. */
  | { status: 'unavailable' };

const statusOf = (error: unknown): number | undefined => (error as AxiosError)?.response?.status;

/** A 409 during refresh leaves the session intact for a later retry. */
const isRefreshRejected = (error: unknown): boolean => {
  const status = statusOf(error);

  return typeof status === 'number' && status >= 400 && status < 500 && status !== 409;
};

const fetchBootUserWithRetry = async (): Promise<TUser> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetchBootUser();
    } catch (error) {
      if (statusOf(error) !== 503 || getAccountStateReason(error) !== 'ACCOUNT_STATE_UNAVAILABLE' || attempt >= 3)
        throw error;
      await new Promise<void>((resolve) => setTimeout(resolve, 300 * 2 ** attempt));
    }
  }
};

/**
 * Verify the session against the API, once, at boot.
 *
 * Never throws: every outcome is a TBootResult the caller branches on. Throwing
 * would surface as the crash page this whole change exists to remove — which is
 * why this does not reuse handleUserDataFlow, whose rejections are right for a
 * form submit and wrong for a page load.
 */
export const bootSession = async (): Promise<TBootResult> => {
  let user: TUser;
  const observedGeneration = readRefreshGeneration();

  try {
    user = await fetchBootUserWithRetry();
  } catch (error) {
    if (statusOf(error) !== 401) {
      // 403, a 5xx, or no response at all. None of those mean "signed out", and
      // wiping local state because the API had a bad minute is strictly worse
      // than letting the user retry.
      return { status: 'unavailable' };
    }

    const accountReason = getAccountStateReason(error);
    if (isTerminalAccountReason(accountReason)) return { status: 'signed-out', reason: accountReason };

    // The common case on returning to a tab: the access token expired but the
    // refresh token is still good. One refresh, one retry.
    try {
      await refreshSession(observedGeneration);
    } catch (refreshError) {
      if (!isRefreshRejected(refreshError)) return { status: 'unavailable' };
      const reason = getAccountStateReason(refreshError);
      return { status: 'signed-out', reason: isTerminalAccountReason(reason) ? reason : 'expired' };
    }

    try {
      user = await fetchBootUserWithRetry();
    } catch (retryError) {
      // A 401 after a successful refresh means the session really is dead.
      if (statusOf(retryError) === 401) {
        const reason = getAccountStateReason(retryError);
        return { status: 'signed-out', reason: isTerminalAccountReason(reason) ? reason : 'expired' };
      }
      return { status: 'unavailable' };
    }
  }

  // Platform membership is otherwise enforced only at sign-in, so without this
  // boot would admit a user that path would have rejected.
  if (!isUserAllowedOnThisPlatform(user)) return { status: 'wrong-platform' };

  const bundle = await fetchSessionBundle(user);

  // Degrading a failed secondary fetch is only safe when the persisted data
  // belongs to the same person. Nothing is partialized, so storage can hold user
  // A while the cookie verifies user B — keeping a slice would hand B some of A's
  // data, and clearing it would read as "not onboarded" and drop them back into
  // onboarding. Neither is acceptable, so this case reports unavailable and the
  // caller blocks on a retry.
  const persistedUser = useAppStore.getState().user;
  const isSameUser = !persistedUser || persistedUser.user_id === user.user_id;
  const hasMissingSlice = user.roles.includes(roles.memberCare) && bundle.memberCare === undefined;

  if (!isSameUser && hasMissingSlice) return { status: 'unavailable' };

  return { status: 'authenticated', bundle };
};
